<template>
  <div>
    <slot>
      <div :class="['TUIKit', isH5 && 'TUIKit-h5']">
        <!-- <div
          v-if="!(isH5 && currentConversationID)"
          class="TUIKit-navbar"
        >
          <div
            v-for="item of navbarList"
            :key="item.id"
            :class="['TUIKit-navbar-item', currentNavbar === item.id && 'TUIKit-navbar-item-active']"
            @click="currentNavbar = item.id"
          >
            {{ item.label }}
          </div>
        </div> -->
        <div class="TUIKit-main-container">
          <div v-if="currentNavbar === 'conversation'" class="TUIKit-main">
            <div
              v-if="!(isH5 && currentConversationID)"
              class="TUIKit-main-aside"
            >
              <TUISearch
                searchType="global"
                :allConversationList="allConversationList"
                v-if="allConversationList.length > 0"
              />
              <TUIConversation
                @saveAtConversationList="saveAtConversationList"
                @saveAllConversationList="saveAllConversationList"
                @clearChatInput="clearChatInput"
              />
            </div>
            <div v-if="!isH5 || currentConversationID" class="TUIKit-main-main">
              <TUIChat
                ref="TUIChatRef"
                :atConversationList="atConversationList"
              >
                <p class="chat-empty">请选择左侧会话，或从业务平台发起聊天。</p>
              </TUIChat>
              <TUIGroup :class="isH5 ? 'chat-popup' : 'chat-aside'" />
              <TUISearch
                :class="isH5 ? 'chat-popup' : 'chat-aside'"
                searchType="conversation"
                :allConversationList="allConversationList"
              />
            </div>
            <TUIGroup
              v-if="isH5 && !currentConversationID"
              class="chat-popup"
            />
            <TUIContact displayType="selectFriend" />
          </div>
          <div v-else-if="currentNavbar === 'contact'" class="TUIKit-main">
            <TUIContact
              displayType="contactList"
              @switchConversation="currentNavbar = 'conversation'"
            />
          </div>
        </div>
      </div>
    </slot>
  </div>
</template>
<script lang="ts" setup>
import { ref, onMounted, onUnmounted, framework } from './adapter-vue';
import { TUILogin } from '@tencentcloud/tui-core';
import {
  TUIStore,
  StoreName,
  TUIConversationService,
} from '@tencentcloud/chat-uikit-engine';
import {
  TUISearch,
  TUIConversation,
  TUIChat,
  TUIContact,
  TUIGroup,
} from './components';
import { isH5 } from './utils/env';
import { isPostMessageSenderOriginAllowed } from '@/utils/post_message_security';

const currentConversationID = ref<string>('');
const currentNavbar = ref<string>('conversation');
const atConversationList = ref<any[]>([]);
const allConversationList = ref<any[]>([]);
const TUIChatRef = ref<InstanceType<typeof TUIChatRef>>();
const navbarList = [
  {
    id: 'conversation',
    label: '会话',
  },
  {
    id: 'contact',
    label: '通讯录',
  },
];
let parentOrigin: string | null = null;
const props = defineProps({
  // When deploying a production environment, you do not need to pass in SDKAppID, userID, and userSig through props.
  // eslint-disable-next-line vue/prop-name-casing
  SDKAppID: {
    type: Number,
    default: 0,
    required: false,
  },
  userID: {
    type: String,
    default: '',
    required: false,
  },
  userSig: {
    type: String,
    default: '',
    required: false,
  },
  conversationID: {
    type: String,
    default: '',
    required: false, // When integrating Chat independently, you need to pass the conversationID
  },
});
const emits = defineEmits(['loginStateChange']);
let isUnmounted = false;

function onCurrentConversationIDUpdated(id: string): void {
  currentConversationID.value = id;
}

onMounted(() => {
  TUIStore.watch(StoreName.CONV, {
    currentConversationID: onCurrentConversationIDUpdated,
  });

  // Default login logic
  login();

  // Modify CallKit style in H5 environment
  modifyCallKitStyle();
  window.addEventListener('message', handleToUserMessage);
  window.addEventListener('beforeunload', sendMessage);
});

onUnmounted(() => {
  isUnmounted = true;
  TUIStore.unwatch(StoreName.CONV, {
    currentConversationID: onCurrentConversationIDUpdated,
  });
  window.removeEventListener('message', handleToUserMessage);
  window.removeEventListener('beforeunload', sendMessage);
});

function handleToUserMessage(event: MessageEvent) {
  if (!isPostMessageSenderOriginAllowed(event.origin)) {
    return;
  }
  const payload = event.data;
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
    return;
  }
  if (typeof (payload as Record<string, unknown>).toUser === 'string' && event.origin) {
    parentOrigin = event.origin;
  }
}

function sendMessage() {
  if (parentOrigin && window.opener) {
    window.opener.postMessage(
      { key: 'PAGE_CLOSED' },
      parentOrigin
    );
  }
}

function saveAllConversationList(conversationList: any) {
  allConversationList.value = conversationList;
}
function saveAtConversationList(conversationList: any) {
  atConversationList.value = conversationList;
}
function clearChatInput() {
  TUIChatRef?.value?.clearInput();
}
function login() {
  const { SDKAppID, userID, userSig, conversationID } = props;
  if (SDKAppID && userID && userSig) {
    emits('loginStateChange', 'loading');
    return Promise.resolve().then(() => TUILogin.login({
      SDKAppID,
      userID,
      userSig,
      useUploadPlugin: true,
      framework,
    }))
      .then(() => {
        if (isUnmounted) return;
        emits('loginStateChange', 'ready');
        if (
          conversationID.startsWith('C2C') ||
          conversationID.startsWith('GROUP')
        ) {
          void Promise.resolve(
            TUIConversationService.switchConversation(conversationID)
          ).catch(() => {});
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      })
      .catch(() => {
        if (!isUnmounted) emits('loginStateChange', 'error');
      });
  }
}

defineExpose({ login });

function modifyCallKitStyle() {
  if (isH5) {
    const bodyStyle = document.getElementsByTagName('body')[0].style;
    bodyStyle.setProperty('--callLeft', '0');
    bodyStyle.setProperty('--callTop', '0');
    bodyStyle.setProperty('--callWidth', '100%');
    bodyStyle.setProperty('--callHeight', '100%');
  }
}
</script>
<style scoped lang="scss">
@import './assets/styles/common';
@import './assets/styles/sample';
.chat-empty {
  padding: 32px;
  color: #909399;
}
</style>
<style lang="scss">
/* stylelint-disable-next-line */
$left: var(--callLeft, calc(50% - 25rem));
/* stylelint-disable-next-line */
$top: var(--callTop, calc(50% - 18rem));
/* stylelint-disable-next-line */
$width: var(--callWidth, 50rem);
/* stylelint-disable-next-line */
$height: var(--callHeight, 36rem);

.callkit-container {
  position: fixed !important;
  left: $left !important;
  top: $top !important;
  width: $width !important;
  height: $height !important;
}
</style>
